import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '@global/store/hooks';

import { useModal } from '@global/hooks/useModal';

import { CancelModalBody } from '@pages/SubscriptionDetails/components/modals';

import { cancelSubscriptionContract } from '@global/store/slices/subscription/extraReducers/cancelSubscriptionContract';
import { getIsDeLocale } from '@global/utils/lang';
import {
  PaybBillingPlanFrequency,
  SubscriptionProduct,
} from '@global/types/types';
import { OrderPrice, PeriodInfo } from '../../types';
import { SiteConfig } from '@src/global/utils/SiteConfig';

export const cancellationReasons = [
  'Z1',
  '11',
  'Z2',
  '24',
  'Z6',
  'Z5',
  '39',
  'Z4',
  '12',
];

export const CANCELLATION_REASON_OTHER = '12';

export type CancellationReason = (typeof cancellationReasons)[number];

interface IProps {
  disabled: boolean;
  subscriptionUid?: string;
  orderPrice: OrderPrice;
  periodInfo: PeriodInfo;
  product: SubscriptionProduct;
  subscriptionFrequency: PaybBillingPlanFrequency;
}

export const SubscriptionInfo: FC<IProps> = ({
  disabled,
  subscriptionUid,
  orderPrice,
  periodInfo,
  product,
  subscriptionFrequency,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { isSpectraBase } = SiteConfig.getConfig();

  const frequency =
    t(`subscription.${subscriptionFrequency.ofUnit}.capitalizedFrequency`) ||
    '';

  const isDe = getIsDeLocale();

  const handleCancel = useCallback(
    async (cancelCode?: string, justification?: string) => {
      dispatch(
        cancelSubscriptionContract({
          t,
          subscriptionUid,
          cancelCode,
          justification,
        })
      );
    },
    [dispatch, t, subscriptionUid]
  );

  const { handleOpen } = useModal({
    content: ({ handleClose }) => (
      <CancelModalBody
        subscriptionUid={subscriptionUid}
        handleCancel={handleCancel}
        handleClose={handleClose}
        nextBillDate={periodInfo?.nextBillDate}
        productData={{
          price: orderPrice.price,
          totalPrice: orderPrice.total,
          maId: product.maId,
          maName: product.maName,
        }}
      />
    ),
    customClass: isSpectraBase ? 'spectrabase' : undefined,
  });

  return (
    <div className="subscription__info-wrapper">
      <span className="subscription__title">
        {t('subDetails.sub.info.title')}
      </span>
      <div className="sub-information">
        <div className="sub-information__column-container">
          <div className="sub-information__column">
            <span className="sub-information__text_gray">
              {t('subDetails.sub.info.subBegan')}
            </span>
            <span className="sub-information__text_black">
              {periodInfo?.subscriptionBegan}
            </span>
          </div>
          <div className="sub-information__column">
            <span className="sub-information__text_gray">
              {t('subDetails.sub.info.costBreakdown.subtitle', { frequency })}
            </span>
            <span className="sub-information__text_black">
              <span className="sub-infromation__text_black sub-information__text_price">
                {isDe ? orderPrice.total.code : orderPrice.price.code}
                {isDe
                  ? orderPrice.total.value.toFixed(2)
                  : orderPrice.price.value.toFixed(2)}
              </span>{' '}
              {t('subDetails.sub.info.costBreakdown.fee')}
            </span>
            <span className="sub-information__text_black">
              <span className="sub-infromation__text_black sub-information__text_price">
                {orderPrice.tax.code}
                {orderPrice.tax.value.toFixed(2)}
              </span>{' '}
              {t('subDetails.sub.info.costBreakdown.tax')}
            </span>
          </div>
        </div>

        <button
          className="subscription__cancel-btn"
          disabled={disabled}
          onClick={handleOpen}
        >
          {t('subDetails.sub.info.cancelCta.btnText')}
        </button>
      </div>
    </div>
  );
};
